import { Controller } from "@hotwired/stimulus"

// Same lookup Turbo uses: the `authenticity_token` cookie first, then the meta tag.
function currentCsrfToken() {
  const fromCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("authenticity_token="))
    ?.split("=")[1]
  return fromCookie ? decodeURIComponent(fromCookie) : document.querySelector('meta[name="csrf-token"]')?.content
}

// Embedded Dodo checkout — the 'embedded' arm of the embedded_checkout A/B.
// Intercepts the paywall form submit, mints the same server-created checkout
// session via a JSON POST, and opens it through the SDK as a fullscreen
// overlay iframe instead of redirecting. Every failure path degrades to the
// control flow: a native form submit that replays the redirect checkout with
// its existing flash messages.
//
// Initialize is app-global (it injects the iframe host) and its onEvent
// callback outlives any single controller instance — the pricing_section
// turbo-frame is replaced after feedback, detaching and reconnecting this
// controller. Events are routed through a module-level pointer to whichever
// instance currently owns an open checkout.
let sdkReady = false
let activeController = null
let DodoPayments = null

// The SDK is only needed by the overlay arm of the checkout, so it comes from its own
// chunk (esbuild code splitting) the first time this controller connects.
async function ensureSdk(mode) {
  if (sdkReady) return true
  DodoPayments ||= (await import("dodopayments-checkout")).DodoPayments
  DodoPayments.Initialize({
    mode, // mirrors DodoClient.test_mode? server-side; "live" in production
    displayType: "overlay",
    onEvent: (event) => activeController?.handleSdkEvent(event)
  })
  sdkReady = true
  return true
}

export default class extends Controller {
  static values = {
    mode: { type: String, default: "live" }
  }

  async connect() {
    this.inFlight = false
    // Until the SDK chunk is in, a submit takes the control path (native redirect).
    this.sdkUsable = false
    try {
      this.sdkUsable = await ensureSdk(this.modeValue)
    } catch {
      this.sdkUsable = false
    }
  }

  disconnect() {
    this.cancelErrorFallback()
    if (activeController === this) activeController = null
  }

  async submit(event) {
    // SDK broken: let the native submit proceed — exact control behavior.
    if (!this.sdkUsable) return
    event.preventDefault()
    if (this.inFlight) return
    this.inFlight = true
    this.setBusy(true)

    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          // The cookie is refreshed on every response (CsrfTokenCookie), the meta
          // tag only when this page was rendered — prefer the one the session
          // currently accepts.
          "X-CSRF-Token": currentCsrfToken()
        },
        // Carries country, zipcode and the form's embedded authenticity_token.
        body: new FormData(this.element)
      })
      if (!response.ok) throw new Error(`checkout session failed: ${response.status}`)

      const { checkout_url: checkoutUrl } = await response.json()
      if (!checkoutUrl) throw new Error("no checkout_url")

      this.checkoutUrl = checkoutUrl
      activeController = this
      DodoPayments.Checkout.open({ checkoutUrl })
      // Button stays disabled while the checkout is up; re-enabled on
      // checkout.closed. On completion the SDK redirects the top window to
      // the return_url (merchant_data token), tearing this page down.
    } catch {
      this.fallbackToRedirect()
    } finally {
      this.inFlight = false
    }
  }

  handleSdkEvent(event) {
    switch (event.event_type) {
      case "checkout.closed":
        this.cancelErrorFallback()
        this.checkoutUsable = false
        this.setBusy(false)
        activeController = null
        break
      case "checkout.payment_page_opened":
      case "checkout.form_ready":
        this.checkoutUsable = true
        this.cancelErrorFallback()
        break
      case "checkout.error":
        // checkout.error is not always fatal: "Failed to initialize wallet"
        // fires when Google Pay can't run and the card form still loads right
        // after it. Only degrade to the hosted checkout page (same session,
        // exactly the control experience) if the form never becomes usable;
        // errors after that point are surfaced by Dodo's own checkout UI.
        if (this.checkoutUsable) break
        this.errorFallbackTimer ||= setTimeout(() => {
          window.location.assign(this.checkoutUrl)
        }, 3000)
        break
      // checkout.opened / checkout.redirect: no-op, the SDK drives navigation.
    }
  }

  cancelErrorFallback() {
    if (this.errorFallbackTimer) clearTimeout(this.errorFallbackTimer)
    this.errorFallbackTimer = null
  }

  fallbackToRedirect() {
    this.setBusy(false)
    this.sdkUsable = false
    // Native submit bypasses submit handlers — no interception loop.
    this.element.submit()
  }

  setBusy(busy) {
    const button = this.element.querySelector('button[type="submit"]')
    if (button) button.disabled = busy
  }
}
